import {
  AgxTextInput,
  AgxSaveChangesButton,
  SAVE_BUTTON_STATES,
} from '@urbanx/agx.ui-components2'
import Panel from 'components/panel/Panel'
import './editRexId.scss'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { AgxToastState } from 'types/commonTypes'
import { isDirtyEqual } from 'utils/compares'
import {
  useGetAgentRexDetails,
  useInvalidateUseGetAgentRexDetails,
  useUpdateAgentRexDetails,
} from '@urbanx/agx.rex.client'
import { isEmailValid } from 'helpers/emailHelper'

interface EditRexIdProps {
  agentId: string
  updateToastState: (toastState: AgxToastState) => void
}

const EditRexId = ({ agentId, updateToastState }: EditRexIdProps) => {
  const { data: agentRexDetails, isPending: isAgentRexDetailsPending } =
    useGetAgentRexDetails(agentId, !!agentId)

  const [rexEmail, setRexEmail] = useState(
    agentRexDetails?.rexUserEmailAddress ?? ''
  )
  const [initialRexEmail, setInitialRexEmail] = useState(
    agentRexDetails?.rexUserEmailAddress ?? ''
  )
  const [saveButtonState, setSaveButtonState] = useState<SAVE_BUTTON_STATES>(
    SAVE_BUTTON_STATES.UNSAVED
  )

  const showSavingError = useCallback(
    (error: any) => {
      console.error('An error occurred while updated the rex id.')

      if (error) console.error(error)

      updateToastState({
        color: 'error',
        message:
          error?.message ?? 'An error occurred while updating the rex id.',
        open: true,
      })
    },
    [updateToastState]
  )

  const invalidateRexAgentDetails = useInvalidateUseGetAgentRexDetails()

  const { updateAgentRexDetails, updateAgentRexDetailsPending } =
    useUpdateAgentRexDetails({
      onSuccess: async (_, inputData) => {
        const savedRexEmail = inputData.rexUserEmailAddress ?? ''
        setRexEmail(savedRexEmail)
        setInitialRexEmail(savedRexEmail)
        setSaveButtonState(SAVE_BUTTON_STATES.SAVED)

        await invalidateRexAgentDetails(agentId)

        updateToastState({
          color: 'success',
          message: 'Rex ID updated successfully',
          open: true,
        })
      },
      onError: (error) => {
        setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
        showSavingError(error)
      },
    })

  const isRexEmailValid = useMemo(() => {
    const hasEmail = !!rexEmail?.trim()
    return hasEmail && isEmailValid(rexEmail)
  }, [rexEmail])

  const isRexIdDirty = useMemo(() => {
    return !isDirtyEqual(rexEmail, initialRexEmail)
  }, [rexEmail, initialRexEmail])

  const saveChangesState = useMemo(() => {
    if (updateAgentRexDetailsPending) return SAVE_BUTTON_STATES.SAVING
    if (saveButtonState === SAVE_BUTTON_STATES.ERROR)
      return SAVE_BUTTON_STATES.ERROR
    if (!isRexEmailValid && isRexIdDirty) return SAVE_BUTTON_STATES.ERROR
    if (isRexIdDirty) return SAVE_BUTTON_STATES.UNSAVED
    return saveButtonState
  }, [
    updateAgentRexDetailsPending,
    saveButtonState,
    isRexEmailValid,
    isRexIdDirty,
  ])

  const onSaveChanges = useCallback(() => {
    const normalisedRexEmail = rexEmail.trim()

    if (
      !isRexIdDirty ||
      !isRexEmailValid ||
      updateAgentRexDetailsPending ||
      normalisedRexEmail.length === 0
    )
      return

    updateAgentRexDetails({
      agentId,
      rexUserEmailAddress: normalisedRexEmail,
    })
  }, [
    agentId,
    rexEmail,
    isRexIdDirty,
    isRexEmailValid,
    updateAgentRexDetailsPending,
    updateAgentRexDetails,
  ])

  useEffect(() => {
    if (!agentRexDetails) return

    const nextRexEmail = agentRexDetails.rexUserEmailAddress ?? ''
    setRexEmail(nextRexEmail)
    setInitialRexEmail(nextRexEmail)
    setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED)
  }, [agentRexDetails])

  useEffect(() => {
    if (isRexIdDirty) {
      setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED)
    }
  }, [isRexIdDirty])

  return (
    <Panel
      title="Rex ID"
      halfWidth
      actionContent={
        <AgxSaveChangesButton
          id="saverexid"
          text="Save Rex ID"
          size="small"
          variant="primary"
          onClick={onSaveChanges}
          state={saveChangesState}
          disabled={
            !isRexIdDirty || !isRexEmailValid || updateAgentRexDetailsPending
          }
        />
      }
    >
      {!isAgentRexDetailsPending && (
        <AgxTextInput
          id="rexIdEmailInput"
          type="email"
          noHeader
          onInputValueChange={({ value }) => {
            setRexEmail(value)
            setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED)
          }}
          defaultValue={
            rexEmail === ''
              ? (agentRexDetails?.rexUserEmailAddress ?? '')
              : rexEmail
          }
          stretch
          required
          placeholder={agentId}
          hideOptionalText
        />
      )}
    </Panel>
  )
}

export default EditRexId
